import { useQuery } from '@tanstack/react-query';
import manufacturingControlTowerService, {
  type ControlTowerQueryParams,
} from '../services/manufacturingControlTower.service';

const unwrap = (res: { data?: { data?: unknown } }) => res.data?.data ?? res.data;

export function useControlTowerSummary(params?: ControlTowerQueryParams, refetchInterval?: number) {
  return useQuery({
    queryKey: ['control-tower', 'summary', params],
    queryFn: async () => unwrap(await manufacturingControlTowerService.getSummary(params)),
    refetchInterval,
  });
}

export function useControlTowerExecution(params?: ControlTowerQueryParams) {
  return useQuery({
    queryKey: ['control-tower', 'execution', params],
    queryFn: async () => unwrap(await manufacturingControlTowerService.getExecutionMonitor(params)),
  });
}

export function useControlTowerFabrication(params?: ControlTowerQueryParams) {
  return useQuery({
    queryKey: ['control-tower', 'fabrication', params],
    queryFn: async () => unwrap(await manufacturingControlTowerService.getFabricationUnits(params)),
  });
}

export function useControlTowerInspections(params?: ControlTowerQueryParams) {
  return useQuery({
    queryKey: ['control-tower', 'inspections', params],
    queryFn: async () => unwrap(await manufacturingControlTowerService.getInspections(params)),
  });
}

export function useControlTowerOutsourced(params?: ControlTowerQueryParams) {
  return useQuery({
    queryKey: ['control-tower', 'outsourced', params],
    queryFn: async () => unwrap(await manufacturingControlTowerService.getOutsourced(params)),
  });
}

export function useControlTowerDelivery(params?: ControlTowerQueryParams) {
  return useQuery({
    queryKey: ['control-tower', 'delivery', params],
    queryFn: async () => unwrap(await manufacturingControlTowerService.getDeliveryReadiness(params)),
  });
}

export function useControlTowerTraceability(params?: ControlTowerQueryParams) {
  return useQuery({
    queryKey: ['control-tower', 'traceability', params],
    queryFn: async () => unwrap(await manufacturingControlTowerService.getTraceability(params)),
  });
}

export function useControlTowerMaterial(params?: ControlTowerQueryParams) {
  return useQuery({
    queryKey: ['control-tower', 'material', params],
    queryFn: async () => unwrap(await manufacturingControlTowerService.getMaterialReadiness(params)),
  });
}

export function useControlTowerProductionRequests() {
  return useQuery({
    queryKey: ['control-tower', 'production-requests'],
    queryFn: async () => unwrap(await manufacturingControlTowerService.getProductionRequests()),
  });
}

export function useControlTowerRouteSheets(params?: ControlTowerQueryParams) {
  return useQuery({
    queryKey: ['control-tower', 'route-sheets', params],
    queryFn: async () => unwrap(await manufacturingControlTowerService.getRouteSheets(params)),
  });
}

export function useControlTowerDocuments() {
  return useQuery({
    queryKey: ['control-tower', 'documents'],
    queryFn: async () => unwrap(await manufacturingControlTowerService.getDocumentCollections()),
  });
}

export function useControlTowerBottlenecks(params?: ControlTowerQueryParams) {
  return useQuery({
    queryKey: ['control-tower', 'bottlenecks', params],
    queryFn: async () => unwrap(await manufacturingControlTowerService.getBottlenecks(params)),
  });
}

export function useControlTowerTimeline(params?: ControlTowerQueryParams) {
  return useQuery({
    queryKey: ['control-tower', 'timeline', params],
    queryFn: async () => unwrap(await manufacturingControlTowerService.getTimeline(params)),
  });
}

export function useControlTowerAnalytics(params?: ControlTowerQueryParams) {
  return useQuery({
    queryKey: ['control-tower', 'analytics', params],
    queryFn: async () => unwrap(await manufacturingControlTowerService.getAnalytics(params)),
  });
}

export function useControlTowerAlerts(params?: ControlTowerQueryParams, refetchInterval?: number) {
  return useQuery({
    queryKey: ['control-tower', 'alerts', params],
    queryFn: async () => unwrap(await manufacturingControlTowerService.getAlerts(params)),
    refetchInterval,
  });
}

export function useControlTowerBrief(params?: ControlTowerQueryParams, refetchInterval?: number) {
  return useQuery({
    queryKey: ['control-tower', 'brief', params],
    queryFn: async () => unwrap(await manufacturingControlTowerService.getBrief(params)),
    refetchInterval,
  });
}

export function useControlTowerAttentionQueue(
  params?: ControlTowerQueryParams,
  refetchInterval?: number
) {
  return useQuery({
    queryKey: ['control-tower', 'attention-queue', params],
    queryFn: async () => unwrap(await manufacturingControlTowerService.getAttentionQueue(params)),
    refetchInterval,
  });
}
